import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import '../../../styles/components/scenario-selector.css';

const ScenarioSelector = ({
  scenarios,
  selectedValue,
  onChange,
  placeholder = "Select Scenario",
  className = '',
  disabledScenarios = [],
  descriptions = {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Convert disabled scenarios to numbers/strings for comparison
  const normalizedDisabled = disabledScenarios.map(id =>
    typeof id === 'string' && !isNaN(id) ? Number(id) : id
  );

  const isDisabled = (scenarioId) => {
    const normalizedId = typeof scenarioId === 'string' && !isNaN(scenarioId) ?
      Number(scenarioId) : scenarioId;
    return normalizedDisabled.includes(normalizedId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (value) => {
    onChange(value);
    setIsOpen(false);
  };

  // Find selected scenario label
  const selectedScenario = scenarios && scenarios.find(s => s.replicaAvg === selectedValue);
  const displayLabel = selectedScenario ? (selectedScenario.scName || `Scenario ${selectedScenario.replicaAvg}`) : placeholder;

  return (
    <div className={`scenario-selector-container ${className}`} ref={containerRef}>
      <div
        className={`custom-select-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        tabIndex={0}
      >
        <span className="selected-value">{displayLabel}</span>
        <div className="arrow-icon">
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1.5L6 6.5L11 1.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div className="custom-select-options">
          {/* Scenarios only - placeholder is not clickable per PDF requirements */}
          {scenarios && scenarios.map((scenario) => {
            const disabled = isDisabled(scenario.replicaAvg);
            const isSelected = scenario.replicaAvg === selectedValue;

            // Get description from the mapping if available
            const scenarioInfo = scenario.scName ? descriptions[scenario.scName] : null;
            const customDescription = scenarioInfo ?
              (scenarioInfo.description || scenarioInfo.summary) :
              null;

            const titleText = (customDescription || scenario.description || scenario.scName);

            const tooltipContent = (
              <div className="scenario-tooltip-content">
                {customDescription || scenario.description || scenario.scName}
              </div>
            );

            return (
              <Tooltip 
                key={scenario.replicaAvg} 
                title={tooltipContent} 
                placement="right"
                mouseEnterDelay={0.5}
                overlayClassName="scenario-custom-tooltip"
              >
                <div
                  className={`custom-option ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                  onClick={() => !disabled && handleSelect(scenario.replicaAvg)}
                >
                  {scenario.scName || `Scenario ${scenario.replicaAvg}`}
                  {disabled ? " (In uso)" : ""}
                </div>
              </Tooltip>
            );
          })}
        </div>
      )}
    </div>
  );
};

ScenarioSelector.propTypes = {
  scenarios: PropTypes.arrayOf(
    PropTypes.shape({
      replicaAvg: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      scName: PropTypes.string
    })
  ),
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabledScenarios: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  descriptions: PropTypes.object
};

export default ScenarioSelector;